// @ts-check

/** @file Narrow transport lookup for already-open immutable project handles. */

import Sync from "../models/sync.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER, projectReplicaIdentity} from "./project-replica-contract.js"

/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @import {ProjectReplicaIdentity} from "./project-replica-contract.js" */

const handlesByProject = new Map()

/**
 * Registers an open captured handle for request-state calculation.
 * @param {ProjectReplicaIdentity} identity Canonical project identity.
 * @param {TenantHandle} handle Immutable open handle.
 */
export function registerProjectReplicaTransportHandle(identity, handle) {
  handlesByProject.set(identityKey(identity), {handle, identity})
}

/**
 * Removes only the exact handle currently registered for this project.
 * @param {ProjectReplicaIdentity} identity Canonical project identity.
 * @param {TenantHandle} handle Expected immutable handle.
 */
export function unregisterProjectReplicaTransportHandle(identity, handle) {
  const key = identityKey(identity)
  const registered = handlesByProject.get(key)
  if (registered?.handle === handle) handlesByProject.delete(key)
}

/**
 * Computes the authoritative non-success queue count in the routed project.
 * @param {{projectId: unknown, userId: unknown}} args Lookup identity.
 * @returns {Promise<number>} Pending or failed mutation count.
 * @rejects {Error} When identity is invalid or the exact replica is not open.
 */
export async function projectReplicaPendingMutationCount({projectId, userId}) {
  const identity = projectReplicaIdentity({projectId, userId})
  const registered = handlesByProject.get(identityKey(identity))
  if (!registered) {
    throw new Error("Project sync has no open replica for the authenticated user/project identity")
  }

  return await projectReplicaPendingMutationCountForHandle(registered.handle)
}

/**
 * Counts non-success intent through one exact captured handle.
 * @param {TenantHandle} handle Immutable open project handle.
 * @returns {Promise<number>} Pending or failed mutation count.
 */
export async function projectReplicaPendingMutationCountForHandle(handle) {
  return await handle.databaseOperation({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const rows = await operation.forModel(Sync).toArray()
    return rows.filter((sync) => {
      const state = sync.state()

      if (state.startsWith("intent-")) return state !== "intent-synced"

      return state !== "success"
    }).length
  })
}

/** Clears transport-only test state after every exact physical database is deleted. */
export function resetProjectReplicaTransportHandlesForTests() {
  handlesByProject.clear()
}

/**
 * Builds an injective in-memory lookup key from canonical identity components.
 * @param {ProjectReplicaIdentity} identity Canonical user/project pair.
 * @returns {string} Exact runtime handle key.
 */
function identityKey(identity) {
  return `${identity.userId}:${identity.projectId}`
}
